import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

export default function FinancialSettings() {
  return (
    <div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">إعدادات التقارير المالية</div>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: '6px 0 0' }}>
          أسعار الوحدات وربط أنواع البنود بكل مشروع.
        </p>
      </div>
      <UnitPriceEditor />
      <ProjectItemTypesEditor />
    </div>
  );
}

function UnitPriceEditor() {
  const { api } = useAuth();
  const toast = useToast();
  const [rows, setRows] = useState([]);

  const load = useCallback(() => {
    api('/financial/unit-prices').then(setRows).catch((e) => toast.error(e.message));
  }, [api, toast]);
  useEffect(() => { load(); }, [load]);

  const set = (i, key, val) => setRows((r) => r.map((row, idx) => idx === i ? { ...row, [key]: val } : row));

  const save = async (row) => {
    try {
      await api(`/financial/unit-prices/${row.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name_ar: row.name_ar, unit: row.unit,
          default_unit_price: row.default_unit_price === '' ? null : row.default_unit_price,
          is_active: !!row.is_active,
        }),
      });
      toast.success('تم حفظ السعر');
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-title" style={{ marginBottom: 12 }}>أسعار الوحدات</div>
      <div className="table-wrap">
        <table>
          <thead><tr><th>الرمز</th><th>الاسم</th><th>الوحدة</th><th>المنطقة</th><th>السعر الافتراضي</th><th>مُفعّل</th><th></th></tr></thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.id}>
                <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>{row.item_type}</td>
                <td><input className="form-input" value={row.name_ar || ''} onChange={(e) => set(i, 'name_ar', e.target.value)} /></td>
                <td><input className="form-input" style={{ width: 90 }} value={row.unit || ''} onChange={(e) => set(i, 'unit', e.target.value)} /></td>
                <td style={{ fontSize: 12 }}>{row.region}</td>
                <td><input className="form-input" style={{ width: 90 }} type="number" value={row.default_unit_price ?? ''} onChange={(e) => set(i, 'default_unit_price', e.target.value)} /></td>
                <td><input type="checkbox" checked={!!row.is_active} onChange={(e) => set(i, 'is_active', e.target.checked)} /></td>
                <td><button className="btn btn-sm btn-primary" onClick={() => save(row)}>حفظ</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProjectItemTypesEditor() {
  const { api } = useAuth();
  const toast = useToast();
  const [projects, setProjects] = useState([]);
  const [prices, setPrices] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [types, setTypes] = useState([]);
  const [newType, setNewType] = useState('');

  useEffect(() => {
    api('/financial/projects').then(setProjects).catch((e) => toast.error(e.message));
    api('/financial/unit-prices').then(setPrices).catch(() => {});
  }, [api, toast]);

  const loadTypes = useCallback(() => {
    if (!projectId) { setTypes([]); return; }
    api(`/financial/project-item-types?project_id=${projectId}`).then(setTypes).catch((e) => toast.error(e.message));
  }, [projectId, api, toast]);
  useEffect(() => { loadTypes(); }, [loadTypes]);

  const addType = async () => {
    if (!newType) return;
    try {
      await api('/financial/project-item-types', { method: 'POST', body: JSON.stringify({ project_id: projectId, item_type: newType }) });
      setNewType('');
      loadTypes();
    } catch (e) { toast.error(e.message); }
  };

  const removeType = async (id) => {
    try { await api(`/financial/project-item-types/${id}`, { method: 'DELETE' }); loadTypes(); }
    catch (e) { toast.error(e.message); }
  };

  const linked = new Set(types.map((t) => t.item_type));
  const available = prices.filter((p) => !linked.has(p.item_type));

  return (
    <div className="card">
      <div className="card-title" style={{ marginBottom: 12 }}>أنواع بنود المشاريع</div>
      <select className="form-select" style={{ maxWidth: 360, marginBottom: 12 }} value={projectId} onChange={(e) => setProjectId(e.target.value)}>
        <option value="">— اختر مشروعاً —</option>
        {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>

      {projectId && (
        <>
          <div className="table-wrap" style={{ marginBottom: 12 }}>
            <table>
              <thead><tr><th>النوع</th><th>اسم خاص</th><th>سعر خاص</th><th></th></tr></thead>
              <tbody>
                {types.length === 0 && <tr><td colSpan={4} className="empty">لا أنواع مرتبطة</td></tr>}
                {types.map((t) => (
                  <tr key={t.id}>
                    <td>{t.unit_price?.name_ar || t.item_type}</td>
                    <td>{t.custom_name || '—'}</td>
                    <td>{t.custom_unit_price ?? '—'}</td>
                    <td><button className="btn btn-sm btn-danger" onClick={() => removeType(t.id)}>حذف</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <select className="form-select" style={{ maxWidth: 300 }} value={newType} onChange={(e) => setNewType(e.target.value)}>
              <option value="">— إضافة نوع —</option>
              {available.map((p) => <option key={p.item_type} value={p.item_type}>{p.name_ar}</option>)}
            </select>
            <button className="btn btn-primary btn-sm" onClick={addType} disabled={!newType}>إضافة</button>
          </div>
        </>
      )}
    </div>
  );
}
